// Glove outline, 0-100 grid. Dot 1 is the start (circled green); TRACE_POINTS closes the
// loop back to it. Structure: 1 cuff start, 2-3 thumb, 4 thumb valley, 5-6/8-9/11-12/14-15
// fingertips, 7/10/13/16 the valleys between them, 17-19 the cuff.
//
// Updated 2026-08-12 from Liv's revised reference. Transcribed by fitting the previous
// reference render against these known coordinates to recover the px-per-unit scale
// (x 8.53, y 18.63 — the grid is drawn with a ~2.19 y-stretch, not square), then applying
// that same transform to the new render. Net effect: the outer edge (14-16) pushes further
// out and down, the thumb side (5-6) and cuff (1, 4, 19) shift right, and 9-10 lift.
const PUZZLE_DOTS = [
  { x: 33.5, y: 59.6, labelX: -2.9, labelY: -0.45 },
  { x: 11.7, y: 35.7, labelX: -1.25, labelY: -0.55 },
  { x: 19.6, y: 33.0, labelX: -0.45, labelY: -1.9 },
  { x: 34.5, y: 42.7, labelX: 0.7, labelY: 0.7 },
  { x: 23.3, y: 18.8, labelX: -0.45, labelY: -1.9 },
  { x: 30.4, y: 18.1, labelX: -0.3, labelY: -1.9 },
  { x: 42.3, y: 32.6, labelX: -0.35, labelY: 0.7 },
  { x: 43.4, y: 15.9, labelX: -0.3, labelY: -1.9 },
  { x: 52.0, y: 15.5, labelX: 0.4, labelY: -1.65 },
  { x: 55.8, y: 32.3, labelX: -0.55, labelY: 0.7 },
  { x: 64.7, y: 18.6, labelX: -0.55, labelY: -1.9 },
  { x: 70.6, y: 20.3, labelX: 0.45, labelY: -1.65 },
  { x: 68.0, y: 35.2, labelX: -0.55, labelY: 0.7 },
  { x: 80.9, y: 26.3, labelX: 0.45, labelY: -1.65 },
  { x: 87.8, y: 27.3, labelX: 0.75, labelY: 0.1 },
  { x: 78.1, y: 40.1, labelX: 0.75, labelY: 0.35 },
  { x: 68.1, y: 57.1, labelX: 0.75, labelY: -0.45 },
  { x: 71.1, y: 79.2, labelX: 0.55, labelY: 0.7 },
  { x: 34.3, y: 79.6, labelX: -1.75, labelY: 0.7 },
];
const TRACE_POINTS = [...PUZZLE_DOTS, PUZZLE_DOTS[0]];

const mountedRoots = new WeakSet();

// True inside the theme editor's preview frame. Editor behaviour differs on purpose:
// the "already played" flag is ignored there, completing the puzzle never writes it,
// and section re-renders (which replace the DOM outright) are cleaned up after.
const designMode = !!(window.Shopify && window.Shopify.designMode);

// The root whose dialog is currently open, and the body overflow value to restore
// when it closes. Tracked at module level so the editor's section:unload — which
// destroys an open dialog without ever calling exitPuzzle — can restore the page.
let activeRoot = null;
let activeOverflowRestore = '';

function safeStorage(storage, action, key, value) {
  if (!storage) return null;

  try {
    if (action === 'get') return storage.getItem(key);
    if (action === 'set') storage.setItem(key, value);
    if (action === 'remove') storage.removeItem(key);
  } catch (error) {
    return null;
  }
  return null;
}

function getStorage(type) {
  try {
    return type === 'session' ? window.sessionStorage : window.localStorage;
  } catch (error) {
    return null;
  }
}

// "reload" = they hit refresh. "navigate" = they followed a link, which includes
// clicking the logo back to the home page. "back_forward" = back button / bfcache.
// The `every_refresh` frequency leans on this to reopen on a refresh but stay shut
// when someone returns to the home page mid-browse. Falls back to the deprecated
// performance.navigation for older iOS Safari, which lacks the entry API.
function getNavigationType() {
  try {
    const entries = performance.getEntriesByType('navigation');
    if (entries && entries.length && entries[0].type) return entries[0].type;
    if (performance.navigation) {
      return performance.navigation.type === 1 ? 'reload' : 'navigate';
    }
  } catch (error) {
    return 'navigate';
  }
  return 'navigate';
}

function emit(root, name, detail = {}) {
  const eventName = `arthur:puzzle:${name}`;
  document.dispatchEvent(new CustomEvent(eventName, { detail }));

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...detail });
  }

  if (window.Sentry && typeof window.Sentry.addBreadcrumb === 'function') {
    window.Sentry.addBreadcrumb({
      category: 'fall-drop-puzzle',
      message: name,
      level: 'info',
      data: detail,
    });
  }

  root.dataset.puzzleState = name;
}

function distanceToSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }

  const t = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy))
  );
  const closest = { x: start.x + t * dx, y: start.y + t * dy };
  return Math.hypot(point.x - closest.x, point.y - closest.y);
}

function shuffle(values) {
  const copy = [...values];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[target]] = [copy[target], copy[index]];
  }
  return copy;
}

function mountPuzzle(root) {
  if (mountedRoots.has(root)) return;
  mountedRoots.add(root);

  const params = new URLSearchParams(window.location.search);
  const campaignKey = root.dataset.campaignKey || 'fall-drop-2-2026-v1';
  const storageKey = `arthur:puzzle:${campaignKey}`;
  const frequency = root.dataset.frequency || 'once_campaign';
  const forceOpen = params.get('puzzle') === '1';
  const reset = params.get('puzzle') === 'reset';
  const disabledForRequest = params.get('puzzle') === 'off';
  const localStorage = getStorage('local');
  const sessionStorage = getStorage('session');
  const sessionScoped = frequency === 'once_session' || frequency === 'every_refresh';
  const storage = sessionScoped ? sessionStorage : localStorage;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reset) {
    safeStorage(localStorage, 'remove', storageKey);
    safeStorage(sessionStorage, 'remove', storageKey);
  }

  const seen = frequency !== 'every_visit' && safeStorage(storage, 'get', storageKey) === '1';
  // `every_refresh` reopens on a hard refresh even once seen, but a plain link
  // navigation back to the home page is still gated by `seen` — so it does not
  // reappear while someone is moving around the store.
  const reopenOnReload = frequency === 'every_refresh' && getNavigationType() === 'reload';
  // `enabled` is the ONLY switch, and only because this is now a static section
  // rendered from the layout — static sections have no eye icon in the theme editor.
  // This is not the old two-switch trap; there is no longer a second control.
  const enabled = root.dataset.enabled !== 'false';
  // In the theme editor (designMode) the `seen` flag is ignored: every settings tweak
  // re-renders the section, and testing should never be gated by an "already played"
  // flag written minutes earlier. The enabled checkbox still governs, so unticking it
  // is visible immediately.
  const shouldOpen = enabled && !disabledForRequest
    && (designMode || forceOpen || reopenOnReload || !seen);
  if (!shouldOpen) return;

  root.dataset.puzzleMounted = '1';

  const stage = root.querySelector('[data-puzzle-stage]');
  const canvas = root.querySelector('[data-puzzle-canvas]');
  const dotsRoot = root.querySelector('[data-puzzle-dots]');
  const play = root.querySelector('[data-puzzle-play]');
  const success = root.querySelector('[data-puzzle-success]');
  const exitButton = root.querySelector('[data-puzzle-exit]');
  const copyButton = root.querySelector('[data-puzzle-copy]');
  const status = root.querySelector('[data-puzzle-status]');
  const discountCode = root.dataset.discountCode || 'WERESOCONNECTED';
  const previousBodyOverflow = document.body.style.overflow;
  const dotElements = [];
  const context = canvas.getContext('2d');

  let state = 'intro';
  let activePointerId = null;
  let nextDotIndex = 1;
  let lastPoint = null;
  let attempts = 0;
  let openedAt = performance.now();
  let exitTimer = null;
  let completionTimer = null;

  function markSeen() {
    // Playing the puzzle inside the theme editor must never poison the real flag —
    // that is exactly how "I completed it and now it never comes back" happens.
    if (designMode) return;
    if (frequency !== 'every_visit') safeStorage(storage, 'set', storageKey, '1');
  }

  function setStatus(message) {
    status.textContent = message;
  }

  function resizeCanvas() {
    const rect = stage.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = rect.width < 500 ? 3.5 : 5;
    context.strokeStyle = getComputedStyle(root).getPropertyValue('--puzzle-accent').trim() || '#21e65b';
  }

  function clearCanvas() {
    const rect = stage.getBoundingClientRect();
    context.setLineDash([]);
    context.clearRect(0, 0, rect.width, rect.height);
  }

  function clearTrace() {
    clearCanvas();
    root.classList.remove('is-completing');
    dotElements.forEach((dot) => dot.classList.remove('is-reached'));
    dotElements[0].classList.remove('is-finish-target');
    nextDotIndex = 1;
    lastPoint = null;
  }

  function stagePoint(event) {
    const rect = stage.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function dotPoint(index) {
    const rect = stage.getBoundingClientRect();
    const dot = TRACE_POINTS[index];
    return { x: (dot.x / 100) * rect.width, y: (dot.y / 100) * rect.height };
  }

  function strokeSegment(start, end, width, opacity) {
    context.save();
    context.globalAlpha = opacity;
    context.lineWidth = width;
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.stroke();
    context.restore();
  }

  function drawSegment(start, end, pressure) {
    const rect = stage.getBoundingClientRect();
    const baseWidth = rect.width < 500 ? 3.5 : 5;
    const normalizedPressure = pressure > 0 ? pressure : 0.5;
    const lineWidth = baseWidth * (0.85 + Math.min(normalizedPressure, 1) * 0.3);

    strokeSegment(start, end, lineWidth + 1.5, 0.12);
    strokeSegment(start, end, lineWidth, 0.94);
  }

  function releasePointer() {
    if (activePointerId === null) return;
    try {
      if (stage.hasPointerCapture(activePointerId)) stage.releasePointerCapture(activePointerId);
    } catch (error) {
      // Pointer capture can already be released by the browser on cancellation.
    }
    activePointerId = null;
  }

  function applyDiscount() {
    const rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const endpoint = window.theme && window.theme.routes
      ? window.theme.routes.cart_update_url
      : `${rootUrl}cart/update.js`;

    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: discountCode }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Discount request failed with ${response.status}`);
        emit(root, 'discount_apply', { status: 'success' });
        return response.json();
      })
      .catch((error) => {
        emit(root, 'discount_apply', { status: 'failed' });
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
          window.Sentry.captureException(error, { tags: { feature: 'fall-drop-puzzle' } });
        }
        return null;
      });
  }

  function showSuccess() {
    if (state === 'success') return;
    window.clearTimeout(completionTimer);
    root.classList.remove('is-completing');
    state = 'success';
    releasePointer();
    markSeen();
    setStatus('Puzzle complete. Discount code ready.');
    play.hidden = true;
    success.hidden = false;
    applyDiscount();
    emit(root, 'complete', {
      attempts,
      duration_ms: Math.round(performance.now() - openedAt),
      placeholder_art: false,
    });
    window.requestAnimationFrame(() => success.focus({ preventScroll: true }));
  }

  function finishPuzzle() {
    if (state !== 'tracing') return;
    state = 'finishing';
    releasePointer();
    dotElements[0].classList.remove('is-finish-target');
    dotElements.forEach((dot) => dot.classList.add('is-reached'));
    setStatus('Puzzle complete.');
    emit(root, 'trace_complete', { attempt: attempts + 1 });

    if (reducedMotion) {
      showSuccess();
      return;
    }
    root.classList.add('is-completing');
    completionTimer = window.setTimeout(showSuccess, 1200);
  }

  function failAttempt(reason) {
    if (state !== 'tracing') return;
    state = 'failed';
    attempts += 1;
    releasePointer();
    root.classList.add('is-failed');
    setStatus('Try again.');
    emit(root, 'attempt_failed', { attempt: attempts, reason });
    window.setTimeout(() => {
      clearTrace();
      root.classList.remove('is-failed');
      state = 'ready';
      setStatus('Puzzle ready.');
      dotElements[0].focus({ preventScroll: true });
    }, reducedMotion ? 0 : 320);
  }

  function exitPuzzle(reason) {
    if (state === 'exited') return;
    state = 'exited';
    window.clearTimeout(completionTimer);
    root.classList.remove('is-completing');
    releasePointer();
    markSeen();
    root.classList.add('is-exiting');
    emit(root, 'exit', { reason });
    window.clearTimeout(exitTimer);
    exitTimer = window.setTimeout(() => {
      document.body.style.overflow = previousBodyOverflow;
      // Dismiss in place. This used to window.location.assign() back to the homepage, which
      // meant closing the puzzle — via the X or via COPY — triggered a full page reload, so
      // the site visibly flashed and re-rendered on entry. The puzzle is an overlay on a
      // page that has already loaded underneath it, so hiding it is all that is needed.
      root.hidden = true;
      root.classList.remove('is-exiting', 'is-open');
      if (activeRoot === root) activeRoot = null;
    }, reducedMotion ? 0 : 180);
  }

  function startTrace(event) {
    if (state !== 'ready' || event.button > 0) return;
    if (!event.target.closest('.arthur-puzzle__dot.is-start')) return;
    event.preventDefault();
    clearTrace();
    state = 'tracing';
    activePointerId = event.pointerId;
    lastPoint = dotPoint(0);
    dotElements[0].classList.add('is-reached');
    try {
      stage.setPointerCapture(activePointerId);
    } catch (error) {
      activePointerId = null;
      state = 'ready';
      return;
    }
    setStatus('Tracing.');
    emit(root, 'start', { attempt: attempts + 1 });
  }

  function processTracePoint(event) {
    const currentPoint = stagePoint(event);
    drawSegment(lastPoint, currentPoint, event.pressure);
    const rect = stage.getBoundingClientRect();
    const hitRadius = event.pointerType === 'touch'
      ? Math.max(30, Math.min(42, rect.width * 0.085))
      : Math.max(22, Math.min(36, rect.width * 0.04));

    while (nextDotIndex < TRACE_POINTS.length) {
      const target = dotPoint(nextDotIndex);
      if (distanceToSegment(target, lastPoint, currentPoint) > hitRadius) break;
      const targetDotIndex = nextDotIndex % PUZZLE_DOTS.length;
      dotElements[targetDotIndex].classList.add('is-reached');
      nextDotIndex += 1;

      if (nextDotIndex === TRACE_POINTS.length - 1) {
        dotElements[0].classList.add('is-finish-target');
        setStatus('Return to 1.');
      }
    }

    lastPoint = currentPoint;
    if (nextDotIndex >= TRACE_POINTS.length) finishPuzzle();
  }

  function moveTrace(event) {
    if (state !== 'tracing' || event.pointerId !== activePointerId) return;
    event.preventDefault();
    const coalescedEvents = typeof event.getCoalescedEvents === 'function'
      ? event.getCoalescedEvents()
      : [];
    const samples = coalescedEvents.length > 0 ? [...coalescedEvents, event] : [event];

    for (const sample of samples) {
      if (state !== 'tracing') break;
      processTracePoint(sample);
    }
  }

  function copyCode() {
    const fallbackCopy = () => {
      const input = document.createElement('textarea');
      input.value = discountCode;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (!copied) throw new Error('Clipboard copy failed');
    };

    const copy = navigator.clipboard && navigator.clipboard.writeText
      ? navigator.clipboard.writeText(discountCode).catch(fallbackCopy)
      : Promise.resolve().then(fallbackCopy);

    copy
      .then(() => {
        copyButton.textContent = 'COPIED';
        setStatus('Discount code copied.');
        emit(root, 'copy', { status: 'success' });
        window.setTimeout(() => exitPuzzle('copy'), reducedMotion ? 0 : 420);
      })
      .catch((error) => {
        copyButton.textContent = 'TRY AGAIN';
        setStatus('Unable to copy discount code.');
        emit(root, 'copy', { status: 'failed' });
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
          window.Sentry.captureException(error, { tags: { feature: 'fall-drop-puzzle' } });
        }
      });
  }

  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = [...root.querySelectorAll('button:not([hidden]), a[href]:not([hidden])')]
      .filter((element) => element.offsetParent !== null && element.tabIndex !== -1);
    if (focusable.length < 2) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  PUZZLE_DOTS.forEach((position, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'arthur-puzzle__dot';
    dot.style.left = `${position.x}%`;
    dot.style.top = `${position.y}%`;
    dot.style.setProperty('--puzzle-label-x', `${position.labelX}em`);
    dot.style.setProperty('--puzzle-label-y', `${position.labelY}em`);
    dot.tabIndex = index === 0 ? 0 : -1;

    const numberLabel = document.createElement('span');
    numberLabel.className = 'arthur-puzzle__dot-number';
    numberLabel.setAttribute('aria-hidden', 'true');
    numberLabel.textContent = index + 1;
    dot.appendChild(numberLabel);

    if (index === 0) {
      dot.classList.add('is-start');
      dot.setAttribute('aria-label', 'Start here');
    } else {
      dot.setAttribute('aria-hidden', 'true');
    }
    dotsRoot.appendChild(dot);
    dotElements.push(dot);
  });

  const revealOrder = shuffle(dotElements.map((dot, index) => index));
  revealOrder.forEach((dotIndex, revealIndex) => {
    const delay = reducedMotion ? 0 : 70 + revealIndex * 42 + Math.random() * 90;
    window.setTimeout(() => dotElements[dotIndex].classList.add('is-visible'), delay);
  });

  root.hidden = false;
  resizeCanvas();
  document.body.style.overflow = 'hidden';
  activeRoot = root;
  activeOverflowRestore = previousBodyOverflow;
  window.requestAnimationFrame(() => root.classList.add('is-open'));
  window.setTimeout(() => {
    if (state !== 'intro') return;
    state = 'ready';
    setStatus('Puzzle ready.');
  }, reducedMotion ? 0 : 70 + dotElements.length * 42 + 120);
  root.focus({ preventScroll: true });
  emit(root, 'view', { forced: forceOpen, placeholder_art: false });

  stage.addEventListener('pointerdown', startTrace);
  stage.addEventListener('pointermove', moveTrace);
  stage.addEventListener('pointerup', () => failAttempt('released'));
  stage.addEventListener('pointercancel', () => failAttempt('cancelled'));
  exitButton.addEventListener('click', () => exitPuzzle('button'));
  copyButton.addEventListener('click', copyCode);
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      exitPuzzle('escape');
      return;
    }
    trapFocus(event);
  });
  window.addEventListener('resize', () => {
    if (state === 'tracing') failAttempt('resized');
    resizeCanvas();
  }, { passive: true });
}

// The puzzle now renders from layout/theme.liquid so it reaches every page, not just
// the home page. If a leftover copy is still enabled in templates/index.json, the home
// page would carry two roots and open two stacked dialogs — so only ever mount one.
let openedOne = false;

function mountAll(container = document) {
  container.querySelectorAll('[data-arthur-puzzle]').forEach((root) => {
    if (openedOne) return;
    mountPuzzle(root);
    if (root.dataset.puzzleMounted === '1') openedOne = true;
  });
}

mountAll();

if (!window.__arthurPuzzleSectionListener) {
  window.__arthurPuzzleSectionListener = true;
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));

  // Theme editor only: every settings tweak re-renders the section, which rips an open
  // dialog's DOM out without exitPuzzle ever running. Untreated, that leaves the page
  // scroll-locked and the one-mount latch stuck, so the puzzle "exits" and can never
  // reopen. Restore the page and unlatch here; the section:load that follows remounts.
  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target.querySelector('[data-arthur-puzzle]')) return;
    if (activeRoot && event.target.contains(activeRoot)) {
      // The open dialog itself is being torn down mid-flight.
      document.body.style.overflow = activeOverflowRestore;
      activeRoot = null;
      openedOne = false;
    } else if (!activeRoot) {
      // A puzzle section reloads while no dialog is open (e.g. closed via the X):
      // unlatch so the fresh markup can mount and open again.
      openedOne = false;
    }
    // A puzzle section reloading while a DIFFERENT copy's dialog is open leaves the
    // latch alone, so the reload cannot stack a second dialog on top.
  });
}
